import Configuration from '@/agilis/util/Configuration'
import dialogFormMixin from '@/agilis/components/DialogForm/dialogFormMixin'
import FormList from '@/agilis/components/Form/FormList/FormList.vue'
import FormTree from '@/agilis/components/Form/FormTree/FormTree.vue'
import JsonEditorText from '@/agilis/components/JsonEditorText/JsonEditorText.vue'
import jsonLogic from '@/plugins/jsonLogic'
import L from 'lodash'
import Vue from 'vue'
import ResizableContainer from '@/agilis/components/ResizableContainer/ResizableContainer.vue'

import { mapActions, mapGetters } from 'vuex'

const d = require('deepdash').default
d(L)

function replaceIdStr (id, prevId, nextId) {
  if (id === prevId) { //  exact match
    return nextId
  } else if (id.indexOf(prevId + '-') === 0) { // begin of the ID
    return id.replace(prevId + '-', nextId + '-')
  } else if ('-' + id.indexOf(prevId + '-') > 0) { // inside the ID
    return id.replace('-' + prevId + '-', '-' + nextId + '-')
  }

  return id
}

function sortObjectKeys (obj) {
  const keys = Object.keys(obj)
  keys.sort()
  keys.forEach(key => {
    const value = obj[key]
    delete obj[key]
    return Object.assign(obj, { [key]: value })
  })
  return obj
}

export default {
  name: 'TreeEditorV2',
  mixins: [dialogFormMixin],
  treeItemLastId: 1,

  components: {
    FormTree,
    JsonEditorText,
    ResizableContainer
  },

  props: {
    document: Object,
    schema: Object,
    applicationId: String,
    customComponents: Object
  },

  /*
  The searchTxtInput is the input insert by the user in the input field for the search
  The searchTxt search in the treeview the input search submitted by the user
  The search has the same behavior as the data table search
  */

  data () {
    return {
      formCustomComponents: {
        FormList
      },
      json: null,
      jsonChanged: false,
      activeNodes: [],
      openNodes: [],
      lastOpen: [],
      forceUnmount: false,
      currentVersion: '',
      autoSave: null,
      expanded: false,
      formTree: [],
      dialogDuplicate: false,
      nodeToCopy: null,
      destinationNodeId: null,
      searchTxt: null,
      searchTxtInput: null
    }
  },

  beforeDestroy () {
    clearInterval(this.autoSave)
  },

  computed: {
    ...mapGetters({
      loading: 'loaderStore/loading',
      selectedContext: 'contextStore/selectedContext',
      userId: 'userDataStore/userId'
    }),

    activeNode () {
      const nodeId = this.activeNodes[0]
      const node = nodeId && this.$refs?.tree?.nodes[nodeId]?.item
      if (node) this.checkNodeConsistency(node)

      return node || null
    },

    activeNodeForm () {
      const node = this.activeNode

      if (node) {
        const schema = this.getSchemaById(node._schema)
        const { columns, formRows } = this.getSchemaForm(schema, node, 'edit')

        const formModel = { model: node, fields: columns, formRows }

        return formModel
      }
      return null
    },

    activeJsonNodeForm () {
      const treeItem = this.activeTreeItem
      const jsonNode = treeItem?.jsonLink.node

      if (jsonNode) {
        let jsonNodeSchema, model
        if (treeItem.jsonLink.schemaId) {
          jsonNodeSchema = this.getSchemaById(treeItem.jsonLink.schemaId)
          if (jsonNodeSchema.type === 'array') {
            const columns = Object.values(jsonNodeSchema.form?.columns)
            const arrayComponent = columns.find(value => value.type === 'datatable' || value.type === 'FormList')
            model = {
              [arrayComponent.id]: L.clone(jsonNode)
            }
          } else {
            model = L.clone(jsonNode)
          }
        } else {
          return null
        }

        if (!this.hasSchemaForm(jsonNodeSchema)) {
          return null
        }

        const { columns, formRows, jsonNodeKey } = this.getSchemaForm(jsonNodeSchema)

        this.checkJsonNodeConsistency(model, jsonNodeSchema)
        if (jsonNodeKey && !model[jsonNodeKey]) {
          model[jsonNodeKey] = treeItem.name
        }

        const formModel = { model: model, fields: columns, formRows }

        return formModel
      }

      return null
    },

    formTreeChildren () {
      if (this.formTree?.length && this.formTree[0]?.children?.length) {
        return this.formTree[0].children
      }
      return []
    },

    documentTitle () {
      return this.document.name ? this.$t(this.document.name) : this.document.id
    },

    documentDescription () {
      return this.document.description && this.$t(this.document.description)
    },

    duplicateDialogTitle () {
      return this.$t('label.treeEditorDuplicateTitle')
    },

    checkinDisabled () {
      return !this.json._checkoutFlag || (this.json._lastUpdatedUser !== this.userId && this.json._checkoutFlag)
    },

    checkoutDisabled () {
      return this.json._lastUpdatedUser !== this.userId && this.json._checkoutFlag
    },

    checkDocumentDisabled () {
      return !this.json._checkoutFlag || this.checkoutDisabled
    },
    jsonLogicContext () {
      return {
        node: this.activeNode
      }
    }
  },

  watch: {
    document: {
      handler (newValue) {
        newValue && this.schema && this.loadTree() // Load dell'ultima versione del doc da server
      },
      immediate: true
    },
    json: {
      handler (newValue, oldValue) {
        if (oldValue) this.jsonChanged = true
        if (newValue) this.loadFormTree()
      },
      deep: true
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'notifyMessage': 'notificationStore/notifyMessage',
      'notifyUserConfirm': 'notificationStore/notifyUserConfirm',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    // /////////////////////////////////////////////////////////////
    // Input/Output utils
    // /////////////////////////////////////////////////////////////
    async loadTree () {
      const loader = { id: 'load-json-document' }

      try {
        this.startLoader(loader)

        const url = `/api/${this.applicationId}/document/${this.document.id}`
        const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)

        const response = (await this.$axios.get(url, config))
        const serverSideJson = response.data || this.createDocumentRoot()

        this.currentVersion = serverSideJson.version

        // Decora con i campi tecnici necessari per la corretta interpretazione nodo => schema
        this.json = this.addTechnicalFields(serverSideJson)

        if (this.json._checkoutFlag && this.json._lastUpdatedUser === this.userId) {
          this.autoSave = setInterval(() => {
            this.autoSaveTree()
          }, this.json._autoSaveMinutes ? this.json._autoSaveMinutes * 60000 : 300000)
        }
      } catch (e) {
        this.notifyError(e)
      }
      this.stopLoader(loader)
    },

    async autoSaveTree () {
      if (this.json._checkoutFlag && this.json._lastUpdatedUser === this.userId) {
        const loader = { id: 'save-json-document' }

        setTimeout(async () => { // Why setTimeout? Make sure blur on typing input to complete the model updates
          // Update technical fields
          this.json._lastUpdatedUser = this.userId
          this.json._lastUpdatedTime = (new Date()).toISOString()

          try {
            this.startLoader(loader)

            // Clean up from not needed technical fields inside each node
            const output = this.removeTechnicalFields(JSON.parse(JSON.stringify(this.json)))
            this.currentVersion = output.version

            // Api
            const url = `/api/${this.applicationId}/document/${this.document.id}/version/${output.version}`
            const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)
            await this.$axios.post(url, output, config)

            // Mak json as saved
            this.jsonChanged = false
          } catch (e) {
            this.notifyError(e)
          }

          this.stopLoader(loader) // TODO: decommentare quando togliamo setTimeout
        }, 0)
      }
    },

    createDocumentRoot () {
      const rootNodeSchema = this.getSchemaById()
      const nodeProperties = Object.entries(rootNodeSchema.properties).reduce((acc, [propName, propConfig]) => {
        acc[propName] = this.getPropertyInitialValue(propName, propConfig, rootNodeSchema)
        return acc
      }, {})

      const newRoot = {
        ...nodeProperties,
        _lastUpdatedTime: null,
        _lastUpdatedUser: null,
        _autoSaveMinutes: 5,
        _id: nodeProperties.id
      }

      setTimeout(() => {
        this.jsonChanged = true
      }, 500)

      return newRoot
    },

    addTechnicalFields (json) {
      L.eachDeep(
        json,
        (value, key, parentValue, ctx) => {
          if (Array.isArray(parentValue)) { // tech fields on array items
            const parent = ctx.parent.parent.value
            this.addNodeTechnicalFields(value, parent)
          }

          // Iterate only on array of children
          return key === 'children' || Array.isArray(parentValue)
        },
        { includeRoot: false }
      )

      return json
    },

    addNodeTechnicalFields (node, parentNode) {
      node = this.updateNodeCompatibility(node)
      const schemaId = this.computeNodeSchemaId(node)
      const ancestors = this.getNodeAncestors(parentNode)

      node._schema = schemaId
      node._id = node.id
      node._ancestors = ancestors

      const childrenProperties = this.getSchemaById(schemaId)?._childrenProperties

      if (childrenProperties) {
        node[childrenProperties] = Object.keys(node).map((key) => [key, node[key]])
      }
    },

    removeTechnicalFields (json) {
      const blackList = [
        '_schema',
        '_id',
        '_ancestors'
      ]

      L.eachDeep(
        json,
        (value, key, parentValue) => {
          if (blackList.indexOf(key) !== -1) {
            delete parentValue[key]
          }

          // Iterate only on array of children
          return key === 'children' || Array.isArray(parentValue)
        },
        { includeRoot: false }
      )

      return json
    },

    // /////////////////////////////////////////////////////////////
    // Tree management utils
    // /////////////////////////////////////////////////////////////
    async addChildNode (node) {
      const childNodeSchema = this.getChildNodeSchema(node)
      const ancestors = this.getNodeAncestors(node)

      const nodeProperties = Object.entries(childNodeSchema.properties).reduce((acc, [propName, propConfig]) => {
        acc[propName] = this.getPropertyInitialValue(propName, propConfig, childNodeSchema)
        return acc
      }, {})

      const newNode = {
        ...nodeProperties,
        id: ancestors.length > 1 ? `${node.id}-` : '', // provide prefix for the ID
        _schema: childNodeSchema.id,
        _ancestors: ancestors
      }

      try {
        const { columns, formRows } = this.getSchemaForm(childNodeSchema, newNode, 'create')
        const formModel = await this.openFormDialog(
          newNode,
          columns,
          formRows
        )

        node.children.push({
          ...newNode,
          ...formModel,
          _id: formModel.id
        })
      } catch (e) {
        // ...
      }
    },

    async addChildNodeFromSelect (node, schemaId) {
      const childNodeSchema = this.getSchemaById(schemaId)
      const ancestors = this.getNodeAncestors(node)

      const nodeProperties = Object.entries(childNodeSchema.properties).reduce((acc, [propName, propConfig]) => {
        acc[propName] = this.getPropertyInitialValue(propName, propConfig, childNodeSchema)
        return acc
      }, {})

      let newNode = {
        ...nodeProperties,
        id: ancestors.length > 1 ? `${node.id}-` : '', // provide prefix for the ID
        _schema: childNodeSchema.id,
        _ancestors: ancestors
      }

      try {
        let formModel = []
        if (this.hasSchemaForm(childNodeSchema)) {
          const { columns, formRows } = this.getSchemaForm(childNodeSchema, newNode, 'create')
          formModel = await this.openFormDialog(
            newNode,
            columns,
            formRows
          )
        }

        if (node.children) {
          node.children.push({
            ...newNode,
            ...formModel,
            _id: schemaId
          })
        } else {
          node.children = []
          node.children.push({
            ...newNode,
            ...formModel,
            _id: schemaId
          })
        }
        this.setFocus(newNode)
      } catch (e) {
        // ...
      }
    },

    async copyNode (node) {
      const parentNode = this.getParentNode(node)
      const nodeIndex = parentNode.children.findIndex(n => n._id === node._id)

      // Copy node and children with name/id replace
      const newNode = JSON.parse(JSON.stringify(node))
      let newId
      let prevId
      L.eachDeep(
        newNode,
        (value, key, parentValue) => {
          if (parentValue === undefined) {
            prevId = value.id

            // Verify newId does not exist yet
            let c = 1
            newId = value.id + 'CPY'
            while (parentNode.children.find(n => n._id === newId)) {
              newId = value.id + 'CPY' + c
              c++
            }

            // Update the copied node
            value.id = newId
            value._id = newId
            value.name = value.name + ' COPY'
            return true
          }

          // in children...
          if (parentValue.id !== newId) {
            switch (key) {
              case 'id':
              case '_id': { // update the ID path
                parentValue[key] = replaceIdStr(value, prevId, newId)
                break
              }

              case '_ancestors': { // update the ancestors
                parentValue[key] = value.map(id => replaceIdStr(id, prevId, newId))
                break
              }

              default:
                // ...
            }
          }

          // Iterate only on array of children
          return key === 'children' || Array.isArray(parentValue)
        },
        { includeRoot: true }
      )

      parentNode.children.splice(nodeIndex + 1, 0, newNode)
      this.setFocus(newNode)
    },

    openDialogDuplicate (node) {
      this.nodeToCopy = node
      this.dialogDuplicate = true
    },

    closeDialogDuplicate () {
      this.nodeToCopy = null
      this.dialogDuplicate = false
    },

    async duplicateNode () {
      if (this.nodeToCopy && this.destinationNodeId) {
        const parentNode = this.getNodeById(this.destinationNodeId)
        if (this.isValidChild(parentNode, this.nodeToCopy)) {
          let newNode = L.cloneDeep(this.nodeToCopy)
          const prefixNewId = parentNode._ancestors ? parentNode.id + '-' : ''
          newNode.id = prefixNewId + this.nodeToCopy.id.split('-').pop()
          this.addNodeTechnicalFields(newNode, parentNode) // set correct ancestors on the node
          newNode = this.addTechnicalFields(newNode) // set correct ancestors on children node
          this.copyRandomNode(newNode, parentNode, this.nodeToCopy.id)
        }
      }
      this.closeDialogDuplicate()
    },

    loadFormTree () {
      this.formTree.splice(0)
      this.formTree.push({
        id: this.json.id,
        name: this.$t(this.json.name),
        children: this.json.children ? this.loadFormTreeChildren(this.json) : undefined
      })
    },

    loadFormTreeChildren (node) {
      return node.children.reduce((acc, child) => {
        acc.push({
          id: child.id,
          name: this.$t(child.name),
          children: child.children ? this.loadFormTreeChildren(child) : undefined
        })
        return acc
      }, [])
    },

    copyRandomNode (node, parentNode, prevId) {
      const nodeIndex = parentNode.children.findIndex(n => n._id === node._id)

      // Copy node and children with name/id replace
      const newNode = L.cloneDeep(node)
      let newId
      let newName
      L.eachDeep(
        newNode,
        (value, key, parentValue) => {
          if (parentValue === undefined) {
            prevId = prevId ?? value.id

            // Verify newId does not exist yet
            let c = 0
            newId = value.id
            newName = value.name
            while (parentNode.children.find(n => n._id === newId)) {
              newId = value.id + 'CPY' + (c === 0 ? '' : c)
              newName = value.name + ' COPY' + (c === 0 ? '' : ' ' + c)
              c++
            }

            // Update the copied node
            value.id = newId
            value._id = newId
            value.name = newName
            return true
          }

          // in children...
          if (parentValue.id !== newId) {
            switch (key) {
              case 'id':
              case '_id': { // update the ID path
                parentValue[key] = replaceIdStr(value, prevId, newId)
                break
              }

              case '_ancestors': { // update the ancestors
                parentValue[key] = value.map(id => replaceIdStr(id, prevId, newId))
                break
              }

              default:
                // ...
            }
          }

          // Iterate only on array of children
          return key === 'children' || Array.isArray(parentValue)
        },
        { includeRoot: true }
      )

      parentNode.children.splice(nodeIndex + 1, 0, newNode)

      this.setFocus(newNode)
    },

    async editNode (node) {
      const schema = this.getSchemaById(node._schema)

      try {
        const parentNode = this.getParentNode(node)
        const nodeIndex = parentNode.children.findIndex(n => n._id === node._id)
        this.checkNodeConsistency(node)

        const { columns, formRows } = this.getSchemaForm(schema, node)
        const formModel = await this.openFormDialog(
          node,
          columns,
          formRows
        )

        parentNode.children[nodeIndex] = {
          ...node,
          formModel
        }
      } catch (e) {
        // ...
      }
    },

    deleteNode (node) {
      const parentNode = this.getParentNode(node)
      parentNode.children = parentNode.children.filter(n => n._id !== node._id)
    },

    async checkin () {
      const loader = { id: 'load-json-document' }

      this.json._lastUpdatedUser = this.userId
      this.json._lastUpdatedTime = (new Date()).toISOString()

      try {
        this.startLoader(loader)

        let output = this.updateChildrenNode(JSON.parse(JSON.stringify(this.json)))
        output = this.removeTechnicalFields(JSON.parse(JSON.stringify(this.json)))
        this.currentVersion = output.version

        const url = Vue.axios.agilisUrlDocumentCheckin(this.applicationId, this.document.id)
        const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)

        await this.$axios.post(url, output, config)

        clearInterval(this.autoSave)
        this.loadTree()
      } catch (e) {
        this.notifyError(e)
      }
      this.stopLoader(loader)
    },

    async checkout () {
      const loader = { id: 'load-json-document' }

      try {
        this.startLoader(loader)

        const url = Vue.axios.agilisUrlDocumentCheckout(this.applicationId, this.document.id)
        const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)

        const response = (await this.$axios.get(url, config))
        const serverSideJson = response.data

        // Decora con i campi tecnici necessari per la corretta interpretazione nodo => schema
        this.json = this.addTechnicalFields(serverSideJson)

        this.autoSave = setInterval(() => {
          this.autoSaveTree()
        }, this.json._autoSaveMinutes ? this.json._autoSaveMinutes * 60000 : 300000)
        this.activeNodes.pop()
      } catch (e) {
        this.notifyError(e)
      }
      this.stopLoader(loader)
    },

    checkNodeConsistency (node) {
      const schema = this.getSchemaById(node._schema)
      schema && Object.entries(schema.properties).forEach(([propName, propConfig]) => {
        if (!node.hasOwnProperty(propName)) {
          Vue.set(node, propName, this.getPropertyInitialValue(propName, propConfig, schema))
        }
      })
    },

    isValidChild (parent, child) {
      const schemaParent = this.getSchemaById(parent._schema)
      if (!schemaParent?.properties?.children?.items?.oneOf.some(schema => schema._ref === child._schema)) {
        const error = {
          message: this.$t('error.treeEditorInvalidParent',
            {
              childType: child._schema,
              parentType: parent._schema || 'document'
            })
        }
        this.notifyError(error)
        return false
      }
      if (!this.canAddChildren(parent)) {
        const error = {
          message: this.$t('error.treeEditorParentDisabled',
            {
              parentType: parent._schema || 'document'
            })
        }
        this.notifyError(error)
        return false
      }
      return true
    },

    getNodeById (nodeId) {
      const node = L.findDeep(
        this.json,
        (value, key) => {
          if (key === 'id' && value === nodeId) {
            return true
          }
        }
      )

      return node.parent
    },

    getParentNode (node) {
      const ancestors = node._ancestors.slice(1)
      let parentNode = this.json
      while (ancestors.length > 0) {
        const parentId = ancestors.shift()
        parentNode = parentNode.children.find(n => n.id === parentId)
      }
      return parentNode
    },

    setFocus (node) {
      setTimeout(() => {
        this.setNodeOpen(node)
        this.activeNodes.pop()
        this.activeNodes.push(node.id)
      }, 0)
    },

    setNodeOpen (node) {
      let openNodes = Array.from(this.$refs.tree.openCache)
      let nodesToOpen = [...node._ancestors, node.id]
      nodesToOpen.forEach(node => {
        if (!openNodes.find(openNode => openNode.id === node)) {
          openNodes.push(node)
        }
      })
      this.openNodes = openNodes
    },

    getNodeAncestors (parentNode) {
      return parentNode._ancestors ? [...parentNode._ancestors, parentNode._id] : [parentNode._id]
    },

    activeNodeBeforeChange (delta) {
      if (delta.id) {
        if (this.activeNode?.children?.length > 0) {
          const tmp = this.activeNode.children[0].id.split('-')
          const oldId = tmp.slice(0, tmp.length - 1).join('-')
          const newId = delta.id

          if (oldId !== newId) {
            // update children
            L.eachDeep(
              this.activeNode,
              (value, key, parentValue) => {
                // in children...
                switch (key) {
                  case 'id':
                  case '_id': { // update the newID in the children path
                    parentValue[key] = replaceIdStr(value, oldId, newId)
                    break
                  }

                  case '_ancestors': { // update the ancestors
                    parentValue[key] = value.map(id => replaceIdStr(id, oldId, newId))
                    break
                  }

                  default:
                    // ...
                }

                // Iterate only on array of children
                return key === 'children' || Array.isArray(parentValue)
              },
              { includeRoot: false }
            )

            /* The code below is a workaround to be able to dynamcally change ids if tree elements */
            // 1) persist in a instance variable the open ids (by replacing the changed value too)
            this.openNodes = Array.from(this.$refs.tree.openCache)
              .map(id => replaceIdStr(id, oldId, newId))

            this.forceUnmount = true // 2) unmount the form and wait a little with a setTimeout to be unmounted
            setTimeout(() => {
              this.activeNode._id = newId // 3) update the technical id on the active node
              this.activeNodes.pop() // 4) reset the activeNodes array
              this.forceUnmount = false // 5) restore the tree and wait a little with a setTimeout to be mounted
              setTimeout(() => {
                this.activeNodes.push(newId) // 6) restore the active node
              }, 100)
            }, 100)
          }
        }
      }

      const fieldConfig = this.activeNodeForm.fields[Object.keys(delta)[0]]
      if (fieldConfig.beforeChange?.method && typeof this[fieldConfig.beforeChange.method] === 'function') {
        this[fieldConfig.beforeChange.method](fieldConfig.beforeChange)
      }

      return delta
    },

    // /////////////////////////////////////////////////////////////
    // Schema management utils
    // /////////////////////////////////////////////////////////////
    getSchemaById (schemaId = this.schema._ref) {
      if (!schemaId) return this.schema
      const s = Object.values(this.schema._defs).find(def => def.id === schemaId)

      if (!s) console.error('Not able to find schema:', schemaId)

      return s
    },

    getChildNodeSchema (node) {
      const nodeSchema = this.getSchemaById(node._schema)

      if (nodeSchema.properties.children.items.type === 'object') {
        return nodeSchema.properties.children.items.type
      } else if (nodeSchema.properties.children.items._ref) {
        return this.getSchemaById(nodeSchema.properties.children.items._ref)
      }
    },

    getSchemaForm (schema, node, mode = null) {
      const columns = schema?.form?.columns || {}
      const formRows = schema?.form?.formRows || []

      /* if (columns.id && node._ancestors) { // TODO: questo è un hardcoding perchè scrive il validator dinamico sul nodo i-esimo
        const prefix = node._ancestors.length > 1 ? `${node._ancestors[node._ancestors.length - 1]}-` : ''
        columns.id = {
          ...columns.id,
          placeholder: prefix,
          rules: {
            unique_id: [this.json, mode],
            regex_id: '^' + prefix + '[A-Z0-9]+$'
          }
        }
      } */

      if (columns.version && !node._ancestors) {
        columns.version = {
          ...columns.version,
          rules: {
            version_higher: this.currentVersion,
            regex: '^(\\d+\\.)?(\\d+\\.)?(\\*|\\d+)$'
          }
        }
      }

      // Inject datatable config form childNode schema
      Object.values(columns)
        .filter((c) => c.type === 'datatable')
        .forEach(c => {
          const fieldId = c.id
          const childSchemaId = schema.properties[fieldId].items._ref
          const childSchema = this.getSchemaById(childSchemaId)

          columns[fieldId].columns = childSchema.form.columns
          columns[fieldId].formRows = childSchema.form.formRows
        })

      return {
        columns,
        formRows
      }
    },

    // /////////////////////////////////////////////////////////////
    // Permissions utils
    // /////////////////////////////////////////////////////////////
    canAddChildren (node) {
      const nodeSchema = this.getSchemaById(node._schema)

      const disabledLogic = nodeSchema?.properties?.children?.disabled?.jsonLogic
      const disabled = disabledLogic ? jsonLogic.apply(disabledLogic, node) : false

      return (nodeSchema?.properties?.children?.type === 'array' && !disabled) || (nodeSchema?._childrenProperties && !disabled)
    },

    canDeleteNode (node) {
      return node.id !== this.document.id
    },

    canEditNode (node) {
      const nodeSchema = this.getSchemaById(node._schema)
      return nodeSchema.form
    },

    canCopyNode (node) {
      const nodeSchema = this.getSchemaById(node._schema)
      return node.id !== this.document.id && nodeSchema.form
    },

    userCanDoDocumentOperation (operation) {
      return Configuration.auth.userCanDoDocumentOperation(this.selectedContext, operation)
    },

    popupWarning (fieldConfig) {
      this.notifyMessage(this.$t(fieldConfig.message))
    },

    hasSchemaForm (jsonNodeSchema) {
      return !!jsonNodeSchema?.form?.columns
    },

    menuData (node) {
      // check what children types can be created by the user
      const nodeSchema = this.getSchemaById(node._schema)
      let children = []
      if (nodeSchema?.properties?.children?.items?.oneOf) {
        children = nodeSchema.properties.children.items.oneOf.map(item => {
          return item
        })
      } else if (nodeSchema?._childrenProperties) {
        children = Object.values(nodeSchema._childrenProperties).map(item => {
          return item
        })
      }
      return children
    },

    computeNodeSchemaId (node) {
      const nodeProperties = Object.keys(node).sort().filter(nodeProperty => nodeProperty !== 'children')
        .filter(nodeProperty => (nodeProperty !== '_schema' && nodeProperty !== '_id' && nodeProperty !== '_ancestors'))
      let schemaId = ''
      Object.values(this.schema._defs).map(schema => {
        const schemaProperties = Object.keys(schema.properties).sort().filter(nodeProperty => nodeProperty !== 'children')
        if (JSON.stringify(nodeProperties) === JSON.stringify(schemaProperties)) {
          schemaId = schema.id
        }
      })
      return schemaId
    },

    updateNodeCompatibility (node) {
      Object.entries(node).forEach(([key, value]) => {
        switch (key) {
          case 'sircleOwer':
            Object.defineProperty(node, `sircleOwner`, Object.getOwnPropertyDescriptor(node, key))
            delete node[key]
        }
      })
      const missingProperties = Object.keys(this.getSchemaById('/schemas/initiative').properties).sort().filter(
        schemaProperty => !Object.keys(node).sort().find(nodeProperty => schemaProperty === nodeProperty))
      if (missingProperties.length === 1 && missingProperties[0] === 'priceList') {
        node.priceList = []
      }
      return node
    },

    toggleTreeview () {
      this.expanded = !this.expanded
      this.$refs.tree.updateAll(this.expanded)
    },

    updateChildrenNode (json) {
      L.eachDeep(
        json,
        (value, key, nodeValue) => {
          if (key === 'children' && this.getSchemaById(nodeValue._schema)._childrenProperties) {
            Object.assign({}, nodeValue, nodeValue[key])

            delete Object.assign(nodeValue, { [this.getSchemaById(nodeValue._schema)._childrenProperties]: nodeValue[0] })[0]
          }
          // Iterate only on array of children
          return key === 'children' || Array.isArray(nodeValue)
        },
        { includeRoot: false }
      )

      return json
    },

    getNewTreeItemId () {
      return this.$options.treeItemLastId++
    },

    createTree () {
      const rootTreeItem = this.createTreeItem(this.rootName, null, this.schema.id, this.innerJson)
      this.createSubTree(rootTreeItem)
      return [rootTreeItem]
    },

    createSubTree (treeItem) {
      if (treeItem.jsonLink.node) {
        for (let key of Object.keys(treeItem.jsonLink.node)) {
          this.addTreeNode(key, treeItem)
        }
      }
      return treeItem
    },

    addTreeNode (jsonKey, treeItemParent) {
      const jsonNodeParent = treeItemParent.jsonLink.node
      const jsonNode = jsonNodeParent[jsonKey]
      if (jsonNode && (typeof jsonNode === 'object')) {
        let newTreeItemName = jsonKey
        let childNodeSchema = null
        let isNodeInForm = false
        if (treeItemParent.jsonLink.schemaId) {
          const parentNodeSchema = this.getSchemaById(treeItemParent.jsonLink.schemaId)
          isNodeInForm = this.isNodeInForm(jsonKey, parentNodeSchema)
          let childSchemaId = parentNodeSchema.properties?.[jsonKey]?._ref
          if (!childSchemaId && !parentNodeSchema.properties?.[jsonKey] && parentNodeSchema.patternProperties) {
            let pattern = Object.keys(parentNodeSchema.patternProperties).find(pattern => jsonKey.match(pattern))
            childSchemaId = pattern ? parentNodeSchema.patternProperties[pattern]._ref : null
          }
          if (childSchemaId) {
            childNodeSchema = this.getSchemaById(childSchemaId)
          }
        }

        if (!isNodeInForm) {
          let newTreeItem = this.createTreeItem(newTreeItemName, treeItemParent, childNodeSchema?.id, jsonNode)
          treeItemParent.children.push(newTreeItem)

          if (typeof jsonNode !== 'string') {
            for (let key of Object.keys(jsonNode)) {
              this.addTreeNode(key, newTreeItem)
            }
          }
        }
      }
    },

    createTreeItem (name, parent, jsonNodeSchemaId, jsonNode) {
      return {
        id: this.getNewTreeItemId(),
        name: this.$t(name),
        children: [],
        parent: parent,
        jsonLink: {
          schemaId: jsonNodeSchemaId,
          node: jsonNode
        }
      }
    },

    sortTreeChildren (treeItem) {
      if (treeItem?.jsonLink?.schemaId) {
        const schema = this.getSchemaById(treeItem.jsonLink.schemaId)
        if (schema?.sortProperties === true) {
          if (treeItem?.children) {
            treeItem.children.sort((a, b) => {
              return a.name.localeCompare(b.name)
            })
          }
          if (treeItem?.jsonLink?.node) {
            treeItem.jsonLink.node = sortObjectKeys(treeItem.jsonLink.node)
          }
        }
      }
    },

    async addJsonNode (treeItem) {
      try {
        const missingProperty = this.getMissingProperties(treeItem)[0]
        const jsonNodeSchema = this.getSchemaById(missingProperty.schemaId)
        const { columns, formRows, jsonNodeKey } = this.getSchemaForm(jsonNodeSchema)
        let model = {}
        if (this.hasSchemaForm(jsonNodeSchema) && jsonNodeSchema.type !== 'array') {
          this.checkJsonNodeConsistency(model, jsonNodeSchema)
          const formModel = await this.openFormDialog(
            model,
            columns,
            formRows
          )
          model = formModel
        } else if (jsonNodeSchema.type === 'array') {
          model = []
        }

        const newJsonNodeKey = jsonNodeKey ? model[jsonNodeKey] || missingProperty.id : missingProperty.id
        if (jsonNodeKey && !L.has(jsonNodeSchema.properties, jsonNodeKey) && L.has(model, jsonNodeKey)) {
          delete model[jsonNodeKey]
        }

        const newJsonNode = model
        const newTreeItemName = newJsonNodeKey
        const newTreeItem = this.createTreeItem(newTreeItemName, treeItem, jsonNodeSchema.id, newJsonNode)
        this.createSubTree(newTreeItem)
        treeItem.children.push(newTreeItem)
        treeItem.jsonLink.node[newJsonNodeKey] = newJsonNode
        this.sortTreeChildren(treeItem)

        this.$emit('jsonChanged', this.innerJson)
      } catch (e) {
        // ...
      }
    },

    copyJsonNode (treeItem) {
      if (treeItem.parent) {
        const jsonNodeParent = treeItem.parent.jsonLink.node
        const jsonNodeKey = treeItem.name
        const jsonNodeSchema = this.getSchemaById(treeItem.jsonLink.schemaId)
        const newJsonNode = L.cloneDeep(treeItem.jsonLink.node)

        let newJsonNodeKey
        let c = 1
        newJsonNodeKey = jsonNodeKey + 'Copy'
        while (Object.keys(jsonNodeParent).find(k => k === newJsonNodeKey)) {
          newJsonNodeKey = jsonNodeKey + 'Copy' + c
          c++
        }
        const newTreeItemName = newJsonNodeKey
        const keyProperty = jsonNodeSchema.form?.key
        if (keyProperty && newJsonNode[keyProperty]) {
          newJsonNode[keyProperty] = newJsonNodeKey
        }

        const newTreeItem = this.createTreeItem(newTreeItemName, treeItem.parent, jsonNodeSchema.id, newJsonNode)
        this.createSubTree(newTreeItem)
        treeItem.parent.children.push(newTreeItem)

        Vue.set(jsonNodeParent, newJsonNodeKey, newJsonNode)
        this.sortTreeChildren(treeItem.parent)

        this.$emit('jsonChanged', this.innerJson)
      }
    },

    deleteJsonNode (treeItem) {
      if (treeItem.parent) {
        const indexToDel = L.findLastIndex(treeItem.parent.children, child => {
          return child.id === treeItem.id
        })
        treeItem.parent.children.splice(indexToDel, 1)
        let jsonNodeParent = treeItem.parent.jsonLink.node
        Vue.delete(jsonNodeParent, treeItem.name)
      }

      this.$emit('jsonChanged', this.innerJson)
    },

    checkJsonNodeBeforeChange (delta) {
      const fieldConfig = this.activeJsonNodeForm.fields[L.findKey(delta)]
      if (fieldConfig?.beforeChange?.method && typeof this[fieldConfig.beforeChange.method] === 'function') {
        this[fieldConfig.beforeChange.method](fieldConfig.beforeChange)
      }

      const treeItem = this.activeTreeItem
      const jsonNodeSchema = this.getSchemaById(treeItem.jsonLink.schemaId)
      let model = this.activeJsonNodeForm.model
      if (fieldConfig.type === 'FormList') {
        if (jsonNodeSchema.type === 'array') {
          model = delta[fieldConfig.id]
        }
      }
      const jsonNodeParent = treeItem.parent?.jsonLink.node
      const keyProperty = jsonNodeSchema.form?.key
      const newJsonNodeKey = model[keyProperty]
      const oldJsonNodeKey = treeItem.name
      if (newJsonNodeKey && oldJsonNodeKey !== newJsonNodeKey) {
        if (jsonNodeParent) {
          delete jsonNodeParent[oldJsonNodeKey]
        }
        treeItem.name = newJsonNodeKey
      }
      if (keyProperty && !jsonNodeSchema.properties[keyProperty] && L.has(model, keyProperty)) {
        delete model[keyProperty]
      }
      if (jsonNodeParent) {
        jsonNodeParent[treeItem.name] = model
      } else {
        this.innerJson = model
      }
      treeItem.jsonLink.node = model
      this.sortTreeChildren(treeItem.parent)

      this.$emit('jsonChanged', this.innerJson)
      return delta
    },

    checkJsonNodeConsistency (jsonNode, jsonNodeSchema) {
      jsonNodeSchema?.properties && Object.entries(jsonNodeSchema.properties).forEach(([propName, propConfig]) => {
        if (!jsonNode.hasOwnProperty(propName)) {
          const initialValue = this.getPropertyInitialValue(propName, propConfig, jsonNodeSchema)
          if (initialValue) {
            L.set(jsonNode, propName, initialValue)
          }
        }
      })
    },

    getPropertyInitialValue (propName, propConfig, jsonNodeSchema) {
      let initialValue
      const defaultValue = jsonNodeSchema?.form?.columns?.[propName]?.defaultValue

      let propertyType = null
      if (propConfig.type) {
        propertyType = propConfig.type
      } else if (propConfig._ref && this.schema[propConfig._ref]) {
        propertyType = this.schema[propConfig._ref].type
      }

      switch (propertyType) {
        case 'array':
          initialValue = L.clone(defaultValue) || []
          break
        case 'object':
          initialValue = L.clone(defaultValue) || {}
          break
        default: initialValue = defaultValue || null
      }
      return initialValue
    },

    getMissingProperties (treeItem) {
      let missingProperties = []
      const jsonNodeSchema = this.getSchemaById(treeItem.jsonLink.schemaId)
      if (jsonNodeSchema?.properties) {
        const complexProperties = Object.keys(jsonNodeSchema.properties).filter((property) => {
          return jsonNodeSchema.properties[property]._ref
        })
        const complexValidProperties = complexProperties?.filter((property) => {
          return !jsonNodeSchema.form?.columns?.[property]
        })
        const complexValidMissingProperties = complexValidProperties?.filter((property) => {
          return !treeItem.jsonLink.node?.[property]
        })
        complexValidMissingProperties.forEach(property => {
          missingProperties.push({
            id: property,
            schemaId: jsonNodeSchema.properties[property]._ref
          })
        })
      }
      if (jsonNodeSchema?.patternProperties) {
        Object.keys(jsonNodeSchema.patternProperties).forEach(property => {
          missingProperties.push({
            id: property,
            schemaId: jsonNodeSchema.patternProperties[property]._ref
          })
        })
      }

      return missingProperties
    },

    checkMissingProperty (treeItem, property) {
      const missingProperties = this.getMissingProperties(treeItem)
      return missingProperties.find(missingProperty => property.match(missingProperty.id))
    },

    // Search method
    handleSearchTxtInput (event) {
      if (event.keyCode === 13) {
        this.searchTxt = this.searchTxtInput
        this.lastOpen = Array.from(this.$refs.tree.openCache)
        this.$refs.tree.updateAll(true)
      }
    },

    handleSearchLensInput () {
      this.searchTxt = this.searchTxtInput
      this.lastOpen = Array.from(this.$refs.tree.openCache)
      this.$refs.tree.updateAll(true)
    },

    handleSearchClearInput () {
      this.searchTxt = ''
      this.searchTxtInput = ''
      this.$refs.tree.updateAll(false)
      this.openNodes = this.lastOpen
    }
  }
}
